import { IndeedTheme, aurora as defaultTheme } from '@indeed/ifl-themes';

type Breakpoint = 'sm' | 'md' | 'lg' | 'xl';
type Orientation = 'landscape' | 'portrait';
type MediaQueryAttributes = 'min-width' | 'max-width' | 'min-height' | 'max-height';
interface StyledProps {
    theme?: IndeedTheme;
}

// Should align with enum values in theme.breakpoints
const BP_MAP = { sm: 0, md: 1, lg: 2, xl: 3 };
const USE_THEME_BREAKPOINTS = false;

// Note: px breakpoint overrides are to fix Safari behavior (see MSGP-3276)
// This should be revisited in the future
const BP_OVERRIDE_PX = ['480px', '768px', '992px', '1280px'];

const paneSizes = {
    // Side pane (used by ConversationListPane and ConversationDetailsPane)
    sidePaneWidth: '370px',

    // Central pane (used by ConversationPane)
    centralPaneWidth: '540px',

    // Central pane expanded (used by ConversationPane when Details pane is hidden)
    centralPaneExpandedWidth: '926px',
};

/**
 * Convert px string into rem string
 * @param px the px string value such as 12px
 * @param basePx the base px
 * @param offset px subtracts offset value before converting to rem, default to 0,
 * so pxToRem('12px', 16.0, 2) equals to pxToRem('14px') and pxToRem('12px', 16.0, -2) equals to pxToRem('10px')
 */
const pxToRem = (valueStr?: string, basePx: number = 16.0, offset: number = 0): string => {
    if (valueStr) {
        // In most updated ifl libraries, they have changed to use rem instead of px, so we don't need to convert
        if (valueStr.includes('rem')) {
            return valueStr;
        }
        const value = valueStr.match(/([-]?[0-9]*\.?[0-9]+)px/)?.[1];
        try {
            return `${
                value && value !== '0' ? `${(parseFloat(value) + offset) / basePx}rem` : '0'
            }`;
        } catch {}
    }

    return '0';
};

/**
 * Creates responsive styles for sx props using media queries
 * @param breakpoint - The breakpoint to use ('sm', 'md', 'lg', 'xl')
 * @param styles - The styles to apply at this breakpoint
 */
const createResponsiveSx = (breakpoint: Breakpoint, styles: Record<string, any>) => ({
    [`@media (min-width: ${getBreakPointValue(breakpoint, defaultTheme as any)})`]: styles,
});

/**
 * Creates sx object with theme access (for backward compatibility during migration)
 * @param styles - The styles object or function
 */
const createSxObject = (
    styles: Record<string, any> | ((theme: IndeedTheme) => Record<string, any>)
) => {
    if (typeof styles === 'function') {
        return styles(defaultTheme as any);
    }
    return styles;
};

/**
 * @deprecated Use sx props directly instead. This is kept for backward compatibility during migration.
 * Wraps the interpolation callback done by emotion to provide guarantee fallback for theme.
 * @param callback
 */
const themeGet = <T extends StyledProps>(
    callback: (props: T & { theme: IndeedTheme }) => Record<string, any>
) => {
    return (props: T): Record<string, any> => {
        const localTheme =
            props.theme && Object.keys(props.theme).length > 0 ? props.theme : defaultTheme;

        return callback({ ...props, ...{ theme: localTheme } });
    };
};

const getBreakPointValue = (breakpoint: Breakpoint, theme: IndeedTheme): string | number =>
    USE_THEME_BREAKPOINTS
        ? theme.breakpoints[BP_MAP[breakpoint]]
        : BP_OVERRIDE_PX[BP_MAP[breakpoint]];

const dimensionMediaQuery = (
    theme: IndeedTheme,
    bp: Breakpoint,
    cssObject: Record<string, any>,
    attr: MediaQueryAttributes = 'min-width',
    orientation?: Orientation
): Record<string, any> => ({
    [`@media (${attr}: ${getBreakPointValue(bp, theme)})${
        orientation ? ` and (orientation: ${orientation})` : ''
    }`]: cssObject,
});

const minWidthMediaQuery = (
    theme: IndeedTheme,
    bp: Breakpoint,
    cssObject: Record<string, any>,
    orientation?: Orientation
): Record<string, any> => dimensionMediaQuery(theme, bp, cssObject, 'min-width', orientation);

const maxWidthMediaQuery = (
    theme: IndeedTheme,
    bp: Breakpoint,
    cssObject: Record<string, any>,
    orientation?: Orientation
): Record<string, any> => dimensionMediaQuery(theme, bp, cssObject, 'max-width', orientation);

const minHeightMediaQuery = (
    theme: IndeedTheme,
    bp: Breakpoint,
    cssObject: Record<string, any>,
    orientation?: Orientation
): Record<string, any> => dimensionMediaQuery(theme, bp, cssObject, 'min-height', orientation);

const maxHeightMediaQuery = (
    theme: IndeedTheme,
    bp: Breakpoint,
    cssObject: Record<string, any>,
    orientation?: Orientation
): Record<string, any> => dimensionMediaQuery(theme, bp, cssObject, 'max-height', orientation);

export {
    paneSizes,
    minWidthMediaQuery,
    maxWidthMediaQuery,
    minHeightMediaQuery,
    maxHeightMediaQuery,
    StyledProps,
    pxToRem,
    themeGet,
    createResponsiveSx,
    createSxObject,
};
